export interface MapLocation {
  latitude: number;
  longitude: number;
}

// A bare coordinate pair, the way "Copy coordinates" in Google Maps hands it over: `24.7136, 46.6753`.
const coordinatesPattern = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

// A maps URL can carry the point in three shapes, in descending order of precision:
//  - `!3d<lat>!4d<lng>` — the place's own pin, present on a /place/ link
//  - `/@<lat>,<lng>,<zoom>z` — the viewport centre, which is only near the pin
//  - `?q=`/`query=`/`ll=`/`daddr=` — how the mobile share sheet and directions links spell it
const urlPinPattern = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/;
const urlCentrePattern = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/;
const urlQueryPattern = /[?&](?:q|query|ll|sll|daddr)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/;

// What the Maps mobile share sheet hands out. It holds no coordinates at all — they only appear
// after the redirect is followed — so it is worth telling apart from an unreadable value.
const shortLinkPattern = /^(?:https?:\/\/)?(?:maps\.app\.goo\.gl|goo\.gl\/maps)\//i;

/**
 * Reads a point out of whatever the user pasted: a Google Maps URL or a raw `lat, lng` pair.
 *
 * Both live in one input because that is how the value arrives — the user copies either the link
 * from the address bar or the coordinates from the right-click menu, and should not have to know
 * which box the app wanted. Returns null when nothing parses or the pair is off the globe, so the
 * caller reports one failure instead of branching per format.
 *
 * A shortened link (maps.app.goo.gl/…) carries no coordinates and cannot be expanded without
 * following the redirect, so it parses as null.
 */
export function parseMapLocation(value: string): MapLocation | null {
  const input = (value ?? '').toString().trim();
  if (!input) {
    return null;
  }

  const coordinates = coordinatesPattern.exec(input);
  if (coordinates) {
    return toMapLocation(coordinates[1], coordinates[2]);
  }

  // A query parameter may arrive percent-encoded (`?q=24.7%2C46.6`); the raw string is kept as the
  // fallback so a malformed escape does not throw away an otherwise readable URL.
  const url = decodeSafely(input);
  const match = urlPinPattern.exec(url) ?? urlCentrePattern.exec(url) ?? urlQueryPattern.exec(url);
  return match ? toMapLocation(match[1], match[2]) : null;
}

/** Whether the value reads as a URL, which decides the wording of the validation error. */
export function looksLikeMapUrl(value: string): boolean {
  return /https?:\/\/|\/|@/.test((value ?? '').toString().trim());
}

/**
 * Whether the value is a shortened Google Maps link. It is a real maps link that simply cannot be
 * read here, so the user is told to open it and copy the full URL rather than that it is invalid.
 */
export function isShortMapLink(value: string): boolean {
  return shortLinkPattern.test((value ?? '').toString().trim());
}

function toMapLocation(lat: string, lng: string): MapLocation | null {
  const latitude = Number(lat);
  const longitude = Number(lng);
  const inRange = Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
  return inRange ? { latitude, longitude } : null;
}

function decodeSafely(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
